import { Discount, Subscription } from 'frontend/types/premium';
import { Route } from 'vue-router';
import { ReferralPageHeaderContext } from 'frontend/types/referral';
import { LessonState } from 'frontend/types/lessons/lesson';
import { RelatedLearningPathInfo } from './types/learningPathV2';

// For creating branded types
// Example: By branding ClassroomIntentKeyForSignup below, we
// ensure it isn't just any string.
export type Brand<K, T> = K & { __brand: T };

export type Optional<T> = T | undefined;
export type Nullable<T> = T | null;
export type Timeout = ReturnType<typeof setTimeout>;

export interface GenericDict {
  [key: string]: any;
}

export interface TypedDict<T> {
  [key: string]: T;
}

export interface RootState {
  response?: { status: number };
}

export type ClassroomIntentKeyForSignup = Brand<string, 'ClassroomIntentKeyForSignup'>;

export interface User {
  username: string;
  first_name: string;
  last_name: string;
  student_country_name?: string;
  is_authenticated?: boolean;
  is_active: boolean;
  is_discussions_moderator?: boolean;
  num_displayable_unread_notifications?: number;
  can_reenable_premium_subscription?: boolean;
  is_during_trial?: boolean;
  is_giftee?: boolean;
  is_staff: boolean;
  hide_admin_features: boolean;
  user_id: number;
  email: string;
  has_brilliant_premium?: boolean;
  is_front_chat_enabled: boolean;
  profile_url: string;
  user_category_id?: number | null;
  is_classroom_teacher: boolean;
  is_classroom_student: boolean;
  is_classroom_user: boolean;
  is_classroom_admin: boolean;
  get_front_chat_hash_premium_users?: string;
  get_front_chat_hash_contact_us?: string;
  is_in_nux: boolean;
  days_since_signup: number | null;
  paywall_cohort: string | null;
  has_current_trial_option: boolean | null;
  new_user_cohort_remaining_seconds: number;
  is_external_lesson_author: boolean;
  referral_url?: string;
  user_category?: string;
  enable_sound_effects: boolean;
  streak: StreakStats;
  front_chat_hash_campgrounds_authors?: string;
  is_reactivating: boolean;
  leagues_show_new_feature_indicator: boolean;
  trial_length: number;
  user_type: string | null;
  last_simson_tutorial_view_time: string | null;
  subscription_tier: PlanType | null;
  show_llp_onboarding_modal?: boolean;
}

export interface UserAvatar {
  size_45: string;
  size_80: string;
  size_190: string;
}

export interface FeatureSwitches {
  codex_codemirror6?: boolean;
  enable_should_we_make_this_next?: boolean;
  enable_hotjar?: boolean;
  enable_courses_search?: boolean;
  enable_google_classrooms?: boolean;
  enable_saved_sets_exporter?: boolean;
  enable_site_maintenance_banner?: boolean;
  sign_in_with_apple?: boolean;
  enable_gift_promo_banner?: boolean;
  enable_teachers_support_chatbot?: boolean;
  enable_campgrounds_author_front_chat?: boolean;
  enable_legacy_javascript_assets?: boolean;
  enable_recaptcha_v3?: boolean;
  enable_savvy_onboarding_flow?: boolean;
  enable_savvy_educators_landingpage?: boolean;
  enable_embedded_checkout?: boolean;
  enable_embedded_checkout_v2?: boolean;
  enable_embedded_checkout_apple_pay?: boolean;
  strictly_check_interactive_width?: boolean;
  enable_asset_editor_line_widget_errors?: boolean;
  use_retool_campgrounds?: boolean;
  enable_codemirror6?: boolean;
  enable_stripe_premium_checkout: boolean;
  enable_stripe_gift_checkout: boolean;
  enable_facebook_auth?: boolean;
  enable_author_snippets?: boolean;
  disable_llp_nux_fallback?: boolean;
  extra_lesson_downloads_for_authors?: boolean;
  enable_cancellation_flow_discount?: boolean;
  enable_math_foundations_tiered_plan?: boolean;
  enable_game_feel_endstate?: boolean;
  enable_icp_nudge?: boolean;
  enable_lihp_nudge?: boolean;
  enable_premium_end_state_nudges?: boolean;
}

export interface Experiment {
  treatment_name: string;
  experiment_state: string;
  is_user_in_population: boolean;
}

export interface FrontendTrackedEvent {
  event_name: string;
  exclude: string[] | null;
  event_properties: {
    [key: string]: string | boolean;
  };
}

export interface AnalyticsInfo {
  amplitude_key: string;
  analytics_identity: string;
  analytics_live: boolean;
  analytics_super_properties: { [key: string]: string | boolean };
  analytics_user_properties: { [key: string]: string | boolean };
  facebook_ad_pixel_id: string;
  google_ad_pixel_id: string;
  google_tag_manager_id: string;
  is_native: boolean;
  is_tracked_user: boolean;
  javascript_tracked_events: FrontendTrackedEvent[];
  logging_enabled: boolean;
  ltv_event_confirm_url: string;
  ltv_event_currency: string;
  ltv_event_id: number;
  ltv_event_ltv: number;
  branch_key: string;
  segment_key: string;
  segment_url: string;
  signed_up: boolean;
  tries: number;
  viewed_paywall: boolean;
}

export interface PrivacyProperties {
  third_party_cookies_disabled: boolean;
  viewed_privacy_banner: boolean;
  under_17: boolean;
  height: number;
}

export interface GlobalState {
  analytics: AnalyticsInfo | undefined;
  feature_switches: FeatureSwitches | undefined;
  experiments: { [key: string]: Experiment } | undefined;
  user: User | undefined;
  privacy_properties: PrivacyProperties | undefined;
  join_classroom_intent_key_for_signup: ClassroomIntentKeyForSignup | undefined;
  loaded_profitwell_snippet: boolean | undefined;
  posted_profitwell_user_email: boolean | undefined;
  google_oauth_client_id: string | undefined;
  discount: Optional<Discount>;
  front_token_campgrounds_authors: string | undefined;
  algolia_proxy_url: string | undefined;
}

export interface ResponseState {
  base_url: string | undefined;
  debug: boolean | undefined;
  message: string | undefined;
  request_path: string | undefined;
  status: number | undefined;
  success: boolean | undefined;
}

export interface Topic {
  name: string;
  slug: string;
  practice_base_url: string;
}

export interface ExportData {
  state?: string;
  signed_url?: string;
  started_time?: string;
  user?: User;
  [key: string]: ExportData[keyof ExportData];
}

export interface ExportDataState {
  state?: string;
  signed_url?: string;
  started_time?: string;
  user?: User;
  [key: string]: ExportDataState[keyof ExportDataState];
}

export interface UserContentBundle {
  bundle_id: number;
  state: string;
  url?: string;
}

export interface WadtudrActions {
  subscriptions: Subscription[];
}

export interface SearchSettings {
  application_id: string;
  readonly_api_key: string;
  problems: {
    index_name: string;
    document_type: string;
  };
}

export interface ClientType {
  is_ios: boolean;
  is_native: boolean;
  is_android: boolean;
  is_mobile: boolean;
  app_version: string;
  class?: string;
}

export interface DjangoMessage {
  level: string;
  message: string;
}

export interface PageHeaderContext {
  show_privacy_policy_alert: boolean;
  signup_extra_params?: string | null;
  messages: DjangoMessage[] | null;
  most_recent_topic_url?: string | null;
  show_community_link_in_header: boolean;
  login_next_url?: string | null;
  path_without_query: string;
  header_signup_next_url?: string | null;
  login_get_params?: string | null;
  search_settings: SearchSettings | undefined;
  topics?: any;
  signup_next_url?: string | null;
  front_chat_token?: string;
  front_chat_hash?: string;
  coupon: Nullable<Coupon>;
  referral_context?: ReferralPageHeaderContext;
  show_referral_modal: boolean;
}

export interface SavedSets {
  exist: boolean;
  url?: string;
}

export interface VersionLink {
  version_name: string;
  version_state: string;
  link: string;
}

export interface ItemAdminInfo {
  edit_url: string;
  responsible_staff_info?: string;
  alternative_link_info?: {
    link: string;
    link_text: string;
  };
  direct_link?: string;
  version_links?: VersionLink[];
  create_quiz_url?: string;
  state?: string;
}

export interface AdminLinkDict {
  link: [string, string];
}

export interface AdminHtmlLinkDict {
  htmlLink: [string, string];
}

export interface AdminEventLinkDict {
  eventName: string;
  linkText: string;
}

export type AdminMenuItem = string | AdminLinkDict | AdminHtmlLinkDict | AdminEventLinkDict;

export interface AdminMenuData {
  items: AdminMenuItem[];
  badges: string[];
}

export interface Collaborator {
  image: string;
  link: string;
  name: string;
}

interface SegmentedContent {
  head: string;
  tail: string;
  raw: string;
}

interface ShouldWeMakeThisNextInfo {
  name?: string;
  blurb?: string;
  vote?: boolean;
}

export enum VersionState {
  DRAFT = 'DRAFT',
  CANDIDATE = 'CANDIDATE',
  MAIN = 'MAIN',
  LEGACY = 'LEGACY',
  RETIRED = 'RETIRED',
  ABANDONED = 'ABANDONED',
}

export interface CourseEditLesson {
  lesson_id: number;
  slug: string;
  lesson_version_id: number;
  version_name: string;
  state: LessonState;
  title: string;
  description: string;
  image?: string;
}

export interface CourseChapter {
  chapter_id?: number;
  name: string;
  slug: string;
  intro_blurb: string;
  lessons: CourseEditLesson[];
}

export interface CourseEditInfo {
  slug: string;
  name: string;
  version_name: string;
  state: VersionState;
  image?: string;
  image_url?: string;
  published?: boolean;
  coming_soon?: boolean;
  description: string;
  color?: string;
  is_editable: boolean;
  course_version_admin_link: string;
  chapters: CourseChapter[];
  number_of_free_chapters: number;
  related_experiment_name?: string;
  related_treatment_name?: string;
}

export interface CourseVersion {
  name: string;
  internal_change_reason: string;
  description: string;
  version_name: string;
  last_modified_ts: string;
  state: VersionState;
  is_editable: boolean;
  created_by_name: string;
  owned_by_name: string;
}

export interface Course {
  absolute_url: string;
  admin_info?: ItemAdminInfo;
  color_v2: string;
  color: string;
  coming_soon: boolean;
  difficulty: number;
  difficulty_name: string;
  course_id: number;
  intro_image_animated?: null;
  intro_image_url: string;
  intro_image_dark_url: string;
  og_image_override_url?: string;
  intro_text: string;
  is_started?: boolean;
  is_complete?: boolean;
  percent_complete?: number;
  name: string;
  notify_when_published: boolean | null;
  nux_url: string | null;
  published: boolean;
  slug: string;
  tags?: string[];
  user_can_preview: boolean;
  visible_chapter_exists: boolean;

  // Individual course page
  collaborators?: Collaborator[];
  concepts_include: string[];
  course_map?: {
    next_steps: Course[];
    prerequisites: Course[];
  };
  description?: SegmentedContent | null;
  has_any_course_progress?: boolean;
  num_concepts_and_exercises?: number;
  prereqs_and_next_steps?: string;
  num_quizzes?: number;
  start_course_link?: string;
  what_you_should_know?: string;
  marketing_title: string;
  marketing_hero_video_url?: string;
  show_should_we_make_this_next: boolean;
  should_we_make_this_next_info: ShouldWeMakeThisNextInfo;
  version_name: string;
  recommended_lesson_slug?: string;

  is_recommendation: boolean;
  use_new_marketing_page_design?: boolean;
}

export interface CoursesListState {
  items: Course[];
}

export interface CourseCategory {
  name: string;
  category_id: number;
}

export interface CourseSubcategory {
  name: string;
  sub_category_id: number;
}

export interface SubcategorizedCoursesState {
  courses: Course[];
  sub_category: CourseSubcategory;
}

export interface CategorizedCoursesState {
  category: CourseCategory;
  courses_by_sub_category: SubcategorizedCoursesState[];
}

export interface GroupedCourses {
  courses: NullableCourse[];
  groupIndex: number;
}

export interface CategoryCoursesMap {
  [category: string]: Course[];
}

export interface CoursesBySubcategory {
  [key: string]: GroupedCourses;
}

export interface CategorizedCoursesBySubcategory {
  category: CourseCategory;
  coursesBySubcategory: CoursesBySubcategory;
}

export interface AutoUserCourseInfo {
  category: UserCategory | null;
  courses: Course[];
  isSpecialNuxCategory?: boolean;
}

export interface CategorizedCoursesListState {
  items: CategorizedCoursesState[];
}

export interface Chapter {
  admin_info?: ItemAdminInfo;
  chapter_id: number;
  chapter_number: number;
  coming_soon: boolean;
  intro_blurb: string;
  name: string;
  notify_when_published: boolean;
  published: boolean;
  quizzes: Quiz[];
  show_paywall: boolean;
  show_quizzes: boolean;
  slug: string;
  lessons_enabled: boolean;
  image: string;
}

interface QuizUserActivity {
  total: number;
  num_complete: number;
}

export type CompletionStatus = 'unstarted' | 'started' | 'completed';

export interface Quiz {
  admin_info?: ItemAdminInfo;
  coming_soon?: boolean;
  course_page_image: string | null;
  description_plain: string;
  description: string;
  link: string;
  slug: string;
  name: string;
  published: boolean;
  quiz_id: number;
  quiz_number: number;
  practice_lessons: Quiz[];
  user_state: CompletionStatus;
  user_activity: QuizUserActivity;
  version_id: Nullable<number>;
  version_name?: string;
}

export interface UserCategory {
  long_description: string;
  long_name: string;
  name?: string;
  premium_cancellation_text: string | null;
  short_description: string;
  short_name: string;
  short_name_plural: string;
  user_category_id: number;
  image_url?: string;
}

export interface UserCategoriesState {
  items: UserCategory[];
}

export interface UserCoursesState {
  special_nux_user_category: UserCategory | null;
  special_nux_user_featured_courses: Course[];
  user_category: UserCategory | null;
  user_featured_courses: Course[];
}

export type NullableCourse = Course | null;

export interface NotificationsDrawerState {
  notifications: any[];
  end_notification_id: number | null;
  start_notification_id: number | null;
  num_extra: number;
  num_new_notifications: number;
}

export interface LoggedOutCoupon {
  expiration_time: number | undefined;
  code: string | undefined;
  coupon_generated: boolean | undefined;
  percent_off: string | undefined;
  rough_num_courses: string | undefined;
}

export interface Coupon {
  code_used_name: string | null;
  percent_off: number;
  remaining_seconds: number;
  trial_length_in_days: number | null;
}

export interface SimplifiedChapter {
  chapter_id: number;
  chapter_number: number;
  name: string;
  quizzes: SimplifiedQuiz[];
  slug: string;
}

export interface SimplifiedQuiz {
  course_page_image: string | null;
  link: string;
  name: string;
  quiz_id: number;
  quiz_number: number;
  user_state: 'unstarted' | 'started' | 'completed';
  user_activity: QuizUserActivity;
}

export interface BasicResult {
  success: boolean;
  reason: string | null;
}

export interface AccountDeactivatedMessage {
  show_just_deactivated_account_message: boolean | undefined;
  permament_delete: boolean | undefined;
  deactivation_subscription_cancelled: boolean | undefined;
  deactivation_subscription_refunded: boolean | undefined;
}

export interface Testimonial {
  author: string;
  rating: number;
  testimonial_id: number;
  testimonial_date?: string;
  source?: string;
  user_category?: string;
  text: string;
}

export interface TeamCategory {
  name: string;
  positions: TeamPosition[];
  icon_url?: string;
  description?: string;
  team_page?: string;
}

export interface TeamPosition {
  name: string;
  url: string;
  location: string;
  work_type: string;
}

export type ExperimentGetter = (experimentName: string, treatmentName: string) => boolean;
export type ActiveExperimentGetter = (treatmentName: string) => string;
export type MobileGetter = ($mq: string) => boolean;
export type PremiumBannerVisiblityGetter = (route: Route) => boolean;

export interface Country {
  name: string;
}

export interface WistiaVideo {
  bind: (eventName: string, callback: () => void) => void;
  unbind: (eventName: string, callback: () => void) => void;
}

export interface RecentLessonInfoState {
  course_name: string;
  course_image_url: string;
  course_percent_complete?: number;
  course_url: string;
  lesson_url: string;
  current_lesson_count: number;
  total_num_of_lessons: number;
  lesson_description: string;
  lesson_image: string;
  lesson_title: string;
  lesson_id: number;
  completed_blocks: number;
  total_blocks: number;
  related_learning_path_info?: RelatedLearningPathInfo;
}

export interface LearningPathsGenericAdminInfo {
  edit_url: string;
}

export interface LearningPathInfo {
  learning_path_id: number;
  name: string;
  slug: string;
  published: boolean;
  image_url: string;
  description: string;
  num_courses?: number;
  blurb?: string;
  course_ids?: number[];
  admin_info: LearningPathsGenericAdminInfo;
  progress?: 'not_started' | 'in_progress' | 'completed';
}

export interface LearningGroupCourse {
  course_id: number;
  slug: string;
  name: string;
  description: string;
  image_url?: string;
  published: boolean;
  coming_soon: boolean;
  notify_when_published: boolean | null;
  color: string;
}

export interface LearningGroup {
  learning_group_id: number;
  name: string;
  published: boolean;
  linked: boolean;
  admin_info: LearningPathsGenericAdminInfo;
  courses: LearningGroupCourse[];
}

export interface RecommendedCourseKeyword {
  keyword_id: number;
  keyword: number;
}

export function unreachableCase(n: never): never {
  throw new Error('Unreachable case');
}

export type ThumbsFeedbackVote = 'positive' | 'negative';

export interface ThumbsFeedbackInputs {
  type: 'quiz' | 'solution' | 'pane' | 'lesson_version' | 'should_we_make_this_next';
  id: number;
  vote: ThumbsFeedbackVote;
  feedback: string;
  quiz_id?: number;
  chapter_id?: number;
  course_id?: number;
}

export interface FigmaImportResult {
  success: boolean;
  processing: boolean;
  import_id: Nullable<number>;
  image_url: Nullable<string>;
  error: Nullable<string>;
  image_width: Nullable<number>;
  image_height: Nullable<number>;
  image_name: Nullable<string>;
  image_slug: Nullable<string>;
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQList {
  faqs: FAQItem[];
}

export interface UserHomeStats {
  lessons_completed: number;
  longest_streak: number;
  problems_attempted: number;
  last_problem_solved_at: number | null;
}

export type StreakDayWeekdayCode = 'Su' | 'M' | 'T' | 'W' | 'Th' | 'F' | 'S';

export interface StreakCalendarDay {
  code: StreakDayWeekdayCode;
  highlighted: boolean;
  current_day: boolean;
}

export interface StreakStats {
  current_streak_length: number;
  problems_attempted_today: number;
  problems_attempted_total: number;
  problems_required_today: number;
  can_increase_streak: boolean;
  calendar: StreakCalendarDay[];
  can_show_streak_animation: boolean;
}

export type StreakNotificationType = 'newstreak' | 'newuser' | 'none';

export type PriceVendor = 'corrily' | 'brilliant';

export interface PaywallPlanObject {
  display_type: string;
  display_price: string;
  display_interval: string;
  display_name: string;
  saving_percentage?: number;
  strikethrough_price?: string;
  renewal_price?: string;
  interval?: string;
  duration?: string;
  is_trial: boolean;
  is_group: boolean;
  is_gift: boolean;
  is_recurring: boolean;
  trial_length_in_days?: number;
  checkout_url: string;
  sort_order: number;
  includes_referral_discount: boolean;
  price_vendor: PriceVendor;
  is_popular: boolean;
  plan_id: number;
  is_math_foundations: boolean;
  is_core_library?: boolean;
  is_all_access?: boolean;
}

export interface PaywallPlansInfo {
  annualPlan?: PaywallPlanObject;
  annualPlanDirect?: PaywallPlanObject;
  annualPlanTrial?: PaywallPlanObject;
  monthlyPlan?: PaywallPlanObject;
  threeMonthPlan?: PaywallPlanObject;
  sixMonthPlan?: PaywallPlanObject;
  twoYearPlan?: PaywallPlanObject;
  threeYearPlan?: PaywallPlanObject;
  fourYearPlan?: PaywallPlanObject;
  lifetimePlan?: PaywallPlanObject;
  groupPlan?: PaywallPlanObject;
  annualPlanIncludesReferralDiscount?: boolean;
  annualPlanIsTrialPlan?: boolean;
  annualMathFoundationsPlan?: PaywallPlanObject;
  annualCoreLibrary?: PaywallPlanObject;
  annualAllAccessPlan?: PaywallPlanObject;
}

export interface ChargebeeInfo {
  siteName: string;
  apiKey: string;
}

/////////////
// Leagues //
/////////////

export interface Competitor {
  uuid: string;
  name: string;
  xp: number;
  me: boolean;
  rank: number;
  avatar_color: string;
  image_url?: string;
  prev_rank?: number;
}

export interface Level {
  position: number;
  name: string;
  icon_url: string;
}

export interface LeagueWeek {
  start: string;
  end: string;
}

export interface Cohort {
  league_week: LeagueWeek;
  competitors: Competitor[];
  image_url: string;
  last_rank_to_advance: Nullable<number>;
  first_rank_to_drop_back: Nullable<number>;
  max_competitors_in_cohort: number;
}

export interface LeagueEndstate {
  title: string;
  subtitle: string;
  lottie_anim: string;
}

export interface LeagueCountdowns {
  seconds_left_in_week: number;
  seconds_left_to_join: number;
}

export interface UserLeague {
  enable_leagues: boolean;
  has_unlocked_leagues: boolean;
  welcome_to_leagues_dismissed: boolean;
  cohort: Nullable<Cohort>;
  endstate: Nullable<LeagueEndstate>;
  competitor: Nullable<Competitor>;
  current_level: Nullable<Level>;
  levels: Level[];
  advancement_message: Nullable<string>;
  show_leaderboard_in_lesson_endstate: boolean;
  countdowns: LeagueCountdowns;
  xp_needed_to_join_leagues: number;
  optOutLockedDetails: Nullable<LeaguesOptOutOrLockedDetails>;
}

export interface LeaguesOptOutOrLockedDetails {
  enable_leagues: boolean;
  lifetime_xp: number;
}

export interface LessonXPDetail {
  name: string;
  coded_name: 'BASE' | 'LESSON_SOLVABLES' | 'FIRST_LESSON_EVER' | 'TOTAL';
  xp: string;
  running_total: number;
}

export interface LessonXPEarned {
  earned_xp: Optional<number>;
  xp_details: Optional<LessonXPDetail[]>;
  zero_xp_message: Optional<string>;
}

export interface LeaguesMeta {
  enable_leagues: boolean;
}

export enum LeaderboardStanding {
  Lead = 'lead',
  Middle = 'middle',
  Behind = 'behind',
}

export enum LeagueUserStatus {
  HAS_JOINED = 'HAS_JOINED',
  CAN_JOIN = 'CAN_JOIN',
  END_OF_WEEK_LOCK = 'END_OF_WEEK_LOCK',
}

export enum ExpressPaymentMethod {
  Paypal = 'paypal_express_checkout',
  Google = 'google_pay',
  Apple = 'apple_pay',
}

export enum ExpressPaymentIdTag {
  Paypal = 'paypal-button',
  Google = 'gpay-button',
  Apple = 'apple-pay-button',
}

export enum ExpressPaymentComponentName {
  Paypal = 'paypal',
  Google = 'google-pay',
  Apple = 'apple-pay',
}

export enum PlanType {
  mathFoundations = 'math-foundations',
  premium = 'premium',
  coreLibrary = 'core-library',
  allAccess = 'all-access',
}
